import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Collapse, Input, Label, Row } from "@/Components/ui";
import { groupReservationService } from "@/api/services/group-reservation.service";
import { GroupReservationRequest } from "@/api/types/group-reservation";
import GroupReservationPageShell from "../GroupReservationPageShell";
import {
  canApproveGroupReservationRequest,
  canCancelGroupReservationApproval,
  canConfirmBankTransfer,
  canRejectGroupReservationRequest,
  canRevertGroupReservationRequest,
  canStartBankTransfer,
  formatGroupReservationDateRange,
  getGroupReservationSourceLabel,
  getGroupReservationStatusLabel,
  getGroupReservationTypeLabel,
} from "./groupReservationAdmin.mjs";
import GroupReservationGuideModal from "./components/GroupReservationGuideModal";

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value?: number | null) => (value ? `${Number(value).toLocaleString()}원` : "-");

const parseReservationIds = (value: string) =>
  value
    .split(/[,\s]+/)
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0);

const InfoItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="mb-3">
    <div className="text-muted fs-12">{label}</div>
    <div className="fw-semibold">{children || "-"}</div>
  </div>
);

const statusColor = (status: string) => {
  if (status === "approved") return "success";
  if (["reverted", "rejected", "canceled"].includes(status)) return "danger";
  if (["awaiting_bank_transfer", "bank_transfer_confirmed"].includes(status)) return "warning";
  if (status === "reservation_created") return "info";
  return "secondary";
};

export default function GroupReservationRequestDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const requestId = Number(params.id);
  const queryClient = useQueryClient();
  const [adminMemo, setAdminMemo] = useState("");
  const [expectedAmount, setExpectedAmount] = useState("");
  const [linkIds, setLinkIds] = useState("");
  const [unlinkIds, setUnlinkIds] = useState("");
  const [bankTransferAmount, setBankTransferAmount] = useState("");
  const [bankTransferDepositor, setBankTransferDepositor] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [revertReason, setRevertReason] = useState("");
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isAdminMemoOpen, setIsAdminMemoOpen] = useState(false);
  const [isReservationLinkOpen, setIsReservationLinkOpen] = useState(false);
  const [isBankTransferOpen, setIsBankTransferOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ["group-reservation", "admin-request", requestId],
    queryFn: () => groupReservationService.getRequest(requestId),
    enabled: Number.isInteger(requestId) && requestId > 0,
  });

  const request = detailQuery.data?.data as GroupReservationRequest | undefined;

  useEffect(() => {
    if (!request) return;
    setAdminMemo(request.admin_memo || "");
    setExpectedAmount(request.expected_amount ? String(request.expected_amount) : "");
    setBankTransferAmount(request.bank_transfer_amount ? String(request.bank_transfer_amount) : "");
    setBankTransferDepositor(request.bank_transfer_depositor || "");
  }, [request?.id]);

  const invalidateRequest = () => {
    queryClient.invalidateQueries({ queryKey: ["group-reservation"] });
  };

  const saveMemoMutation = useMutation({
    mutationFn: () =>
      groupReservationService.updateRequest(requestId, {
        admin_memo: adminMemo || null,
        expected_amount: expectedAmount ? Number(expectedAmount) : null,
      }),
    onSuccess: () => {
      toast.success("관리 메모를 저장했습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "관리 메모 저장에 실패했습니다."),
  });

  const linkMutation = useMutation({
    mutationFn: () => groupReservationService.linkReservations(requestId, { reservation_ids: parseReservationIds(linkIds) }),
    onSuccess: () => {
      toast.success("예약 건을 연결했습니다.");
      setLinkIds("");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "예약 연결에 실패했습니다."),
  });

  const unlinkMutation = useMutation({
    mutationFn: () =>
      groupReservationService.unlinkReservations(requestId, { reservation_ids: parseReservationIds(unlinkIds) }),
    onSuccess: () => {
      toast.success("예약 연결을 해제했습니다.");
      setUnlinkIds("");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "예약 연결 해제에 실패했습니다."),
  });

  const startBankMutation = useMutation({
    mutationFn: () => groupReservationService.startBankTransfer(requestId),
    onSuccess: () => {
      toast.success("입금 안내를 시작했습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "입금 안내 시작에 실패했습니다."),
  });

  const confirmBankMutation = useMutation({
    mutationFn: () =>
      groupReservationService.confirmBankTransfer(requestId, {
        bank_transfer_amount: Number(bankTransferAmount),
        bank_transfer_depositor: bankTransferDepositor,
      }),
    onSuccess: () => {
      toast.success("입금 확인을 기록했습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "입금 확인 기록에 실패했습니다."),
  });

  const approveMutation = useMutation({
    mutationFn: () => groupReservationService.approve(requestId),
    onSuccess: () => {
      toast.success("단체예약 신청을 승인했습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "승인에 실패했습니다."),
  });

  const cancelApprovalMutation = useMutation({
    mutationFn: () => groupReservationService.cancelApproval(requestId),
    onSuccess: () => {
      toast.success("승인을 취소했습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "승인 취소에 실패했습니다."),
  });

  const rejectMutation = useMutation({
    mutationFn: () => groupReservationService.reject(requestId, { reject_reason: rejectReason }),
    onSuccess: () => {
      toast.success("단체예약 신청을 반려했습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "반려 처리에 실패했습니다."),
  });

  const revertMutation = useMutation({
    mutationFn: () => groupReservationService.revert(requestId, { revert_reason: revertReason }),
    onSuccess: () => {
      toast.success("연결 예약을 되돌렸습니다.");
      invalidateRequest();
    },
    onError: (error: any) => toast.error(error?.message || "되돌림 처리에 실패했습니다."),
  });

  const submitLink = () => {
    const reservationIds = parseReservationIds(linkIds);
    if (!reservationIds.length) {
      toast.error("연결할 예약 ID를 입력해주세요.");
      return;
    }
    linkMutation.mutate();
  };

  const submitUnlink = () => {
    const reservationIds = parseReservationIds(unlinkIds);
    if (!reservationIds.length) {
      toast.error("해제할 예약 ID를 입력해주세요.");
      return;
    }
    unlinkMutation.mutate();
  };

  const submitConfirmBank = () => {
    if (!Number(bankTransferAmount) || !bankTransferDepositor.trim()) {
      toast.error("입금자명과 입금 확인 금액을 입력해주세요.");
      return;
    }
    confirmBankMutation.mutate();
  };

  const submitReject = () => {
    if (!rejectReason.trim()) {
      toast.error("반려 사유를 입력해주세요.");
      return;
    }
    rejectMutation.mutate();
  };

  const submitRevert = () => {
    if (!revertReason.trim()) {
      toast.error("되돌림 사유를 입력해주세요.");
      return;
    }
    revertMutation.mutate();
  };

  const actionBusy =
    saveMemoMutation.isPending ||
    linkMutation.isPending ||
    unlinkMutation.isPending ||
    startBankMutation.isPending ||
    confirmBankMutation.isPending ||
    approveMutation.isPending ||
    cancelApprovalMutation.isPending ||
    rejectMutation.isPending ||
    revertMutation.isPending;

  if (detailQuery.isLoading) {
    return (
      <GroupReservationPageShell>
        <BreadCrumb title="단체예약 신청 상세" pageTitle="예약" />
        <Card>
          <CardBody className="py-5 text-center text-muted">신청 정보를 불러오는 중입니다.</CardBody>
        </Card>
      </GroupReservationPageShell>
    );
  }

  if (detailQuery.isError || !request) {
    return (
      <GroupReservationPageShell>
        <BreadCrumb title="단체예약 신청 상세" pageTitle="예약" />
        <Card>
          <CardBody className="py-5 text-center text-danger">신청 정보를 불러오지 못했습니다.</CardBody>
        </Card>
      </GroupReservationPageShell>
    );
  }

  return (
    <GroupReservationPageShell>
      <BreadCrumb title="단체예약 신청 상세" pageTitle="예약" />
      <GroupReservationGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />

      <div className="mb-3 d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-sm-between gap-2">
        <Button
          color="soft-secondary"
          className="w-full sm:w-auto"
          onClick={() => navigate("/group-reservation-requests")}
        >
          목록으로
        </Button>
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
          <Button color="soft-primary" className="w-full sm:w-auto" onClick={() => setIsGuideOpen(true)}>
            <HelpCircle size={16} className="me-1" />
            사용 안내
          </Button>
          <Badge className="w-full justify-center sm:w-auto sm:justify-start" color={statusColor(request.status)}>
            {getGroupReservationStatusLabel(request.status)}
          </Badge>
        </div>
      </div>

      <Row className="g-3">
        <Col xl={7}>
          <Card>
            <CardHeader>신청 정보</CardHeader>
            <CardBody>
              <Row>
                <Col md={4}><InfoItem label="접수번호">{request.request_code}</InfoItem></Col>
                <Col md={4}><InfoItem label="접수경로">{getGroupReservationSourceLabel(request)}</InfoItem></Col>
                <Col md={4}><InfoItem label="접수일">{formatDateTime(request.created_at)}</InfoItem></Col>
                <Col md={4}><InfoItem label="단체명">{request.group_name}</InfoItem></Col>
                <Col md={4}><InfoItem label="단체 타입">{getGroupReservationTypeLabel(request)}</InfoItem></Col>
                <Col md={4}><InfoItem label="희망일">{formatGroupReservationDateRange(request)}</InfoItem></Col>
                <Col md={4}><InfoItem label="희망 시간대">{request.preferred_time || "-"}</InfoItem></Col>
                <Col md={4}><InfoItem label="총 인원">{request.headcount?.toLocaleString()}명</InfoItem></Col>
                <Col md={4}>
                  <InfoItem label="성인/아동/인솔">
                    {request.group_type === "academy"
                      ? `${request.adult_count || 0}/${request.child_count || 0}/${request.leader_count || 0}`
                      : "-"}
                  </InfoItem>
                </Col>
                <Col md={4}><InfoItem label="참고 상품">{request.preferred_package_name_snapshot || "-"}</InfoItem></Col>
                <Col md={4}><InfoItem label="신청자">{request.applicant_name}</InfoItem></Col>
                <Col md={4}><InfoItem label="연락처">{request.applicant_phone}</InfoItem></Col>
                <Col md={4}><InfoItem label="이메일">{request.applicant_email || "-"}</InfoItem></Col>
              </Row>
              <InfoItem label="고객 요청사항">{request.request_memo || "-"}</InfoItem>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>예상 금액 및 수수료</CardHeader>
            <CardBody>
              {request.selected_options?.length ? (
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-3">
                    <thead className="table-light">
                      <tr>
                        <th>옵션</th>
                        <th>주중/주말</th>
                        <th>수량</th>
                        <th className="text-end">금액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {request.selected_options.map((option) => (
                        <tr key={option.id}>
                          <td>{option.name_snapshot}</td>
                          <td>
                            {formatAmount(option.weekday_price_snapshot)} / {formatAmount(option.weekend_price_snapshot)}
                          </td>
                          <td>{option.quantity?.toLocaleString()}개</td>
                          <td className="text-end">{formatAmount(option.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mb-3 text-muted">선택된 단체예약 옵션이 없습니다.</div>
              )}
              <Row>
                <Col md={4}><InfoItem label="예상 옵션 합계">{formatAmount(request.estimated_total_amount)}</InfoItem></Col>
                <Col md={4}><InfoItem label="대리점 수수료율">{request.agency_commission_rate_snapshot ? `${request.agency_commission_rate_snapshot}%` : "-"}</InfoItem></Col>
                <Col md={4}><InfoItem label="대리점 수수료">{formatAmount(request.agency_commission_amount)}</InfoItem></Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>관리 메모</span>
              <Button
                color="soft-secondary"
                size="sm"
                aria-controls="group-reservation-admin-memo"
                aria-expanded={isAdminMemoOpen}
                aria-label={`관리 메모 ${isAdminMemoOpen ? "닫기" : "펼치기"}`}
                onClick={() => setIsAdminMemoOpen((previous) => !previous)}
              >
                {isAdminMemoOpen ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {isAdminMemoOpen ? "닫기" : "펼치기"}
              </Button>
            </CardHeader>
            <Collapse id="group-reservation-admin-memo" isOpen={isAdminMemoOpen}>
              <CardBody>
                <Row className="g-3">
                  <Col md={4}>
                    <Label htmlFor="expected-amount">예상 금액</Label>
                    <Input
                      id="expected-amount"
                      type="number"
                      min="0"
                      value={expectedAmount}
                      onChange={(event) => setExpectedAmount(event.target.value)}
                    />
                  </Col>
                  <Col md={8}>
                    <Label htmlFor="admin-memo">내부 메모</Label>
                    <Input
                      id="admin-memo"
                      type="textarea"
                      rows={4}
                      value={adminMemo}
                      onChange={(event) => setAdminMemo(event.target.value)}
                    />
                  </Col>
                </Row>
                <Button className="mt-3" onClick={() => saveMemoMutation.mutate()} disabled={actionBusy}>
                  저장
                </Button>
              </CardBody>
            </Collapse>
          </Card>
        </Col>

        <Col xl={5}>
          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>예약 연결</span>
              <Button
                color="soft-secondary"
                size="sm"
                aria-controls="group-reservation-link"
                aria-expanded={isReservationLinkOpen}
                aria-label={`예약 연결 ${isReservationLinkOpen ? "닫기" : "펼치기"}`}
                onClick={() => setIsReservationLinkOpen((previous) => !previous)}
              >
                {isReservationLinkOpen ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {isReservationLinkOpen ? "닫기" : "펼치기"}
              </Button>
            </CardHeader>
            <Collapse id="group-reservation-link" isOpen={isReservationLinkOpen}>
              <CardBody>
                <InfoItem label="현재 연결 예약 수">{request.linked_reservation_count || 0}건</InfoItem>
                <Label htmlFor="link-reservation-ids">연결할 예약 ID</Label>
                <Input
                  id="link-reservation-ids"
                  value={linkIds}
                  onChange={(event) => setLinkIds(event.target.value)}
                  placeholder="예: 12345, 12346"
                />
                <Button className="mt-2" color="soft-primary" onClick={submitLink} disabled={actionBusy}>
                  예약 연결
                </Button>

                <Label className="mt-4" htmlFor="unlink-reservation-ids">해제할 예약 ID</Label>
                <Input
                  id="unlink-reservation-ids"
                  value={unlinkIds}
                  onChange={(event) => setUnlinkIds(event.target.value)}
                  placeholder="예: 12345"
                />
                <Button className="mt-2" color="soft-danger" onClick={submitUnlink} disabled={actionBusy}>
                  연결 해제
                </Button>
              </CardBody>
            </Collapse>
          </Card>

          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>입금 확인</span>
              <Button
                color="soft-secondary"
                size="sm"
                aria-controls="group-reservation-bank-transfer"
                aria-expanded={isBankTransferOpen}
                aria-label={`입금 확인 ${isBankTransferOpen ? "닫기" : "펼치기"}`}
                onClick={() => setIsBankTransferOpen((previous) => !previous)}
              >
                {isBankTransferOpen ? <ChevronUp size={16} className="me-1" /> : <ChevronDown size={16} className="me-1" />}
                {isBankTransferOpen ? "닫기" : "펼치기"}
              </Button>
            </CardHeader>
            <Collapse id="group-reservation-bank-transfer" isOpen={isBankTransferOpen}>
              <CardBody>
                <Row>
                  <Col md={4}><InfoItem label="은행">{request.site_bank_name_snapshot || "-"}</InfoItem></Col>
                  <Col md={4}><InfoItem label="계좌번호">{request.site_bank_account_number_snapshot || "-"}</InfoItem></Col>
                  <Col md={4}><InfoItem label="예금주">{request.site_bank_account_holder_snapshot || "-"}</InfoItem></Col>
                </Row>
                <Button
                  color="soft-primary"
                  onClick={() => startBankMutation.mutate()}
                  disabled={actionBusy || !canStartBankTransfer(request)}
                >
                  입금 안내 시작
                </Button>

                <Row className="g-3 mt-2">
                  <Col md={6}>
                    <Label htmlFor="bank-depositor">입금자명</Label>
                    <Input
                      id="bank-depositor"
                      value={bankTransferDepositor}
                      onChange={(event) => setBankTransferDepositor(event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label htmlFor="bank-amount">입금 확인 금액</Label>
                    <Input
                      id="bank-amount"
                      type="number"
                      min="1"
                      value={bankTransferAmount}
                      onChange={(event) => setBankTransferAmount(event.target.value)}
                    />
                  </Col>
                </Row>
                <div className="mt-2 text-muted fs-12">
                  현재 기록: {request.bank_transfer_depositor || "-"} / {formatAmount(request.bank_transfer_amount)}
                </div>
                <Button
                  className="mt-3"
                  color="soft-success"
                  onClick={submitConfirmBank}
                  disabled={actionBusy || !canConfirmBankTransfer(request)}
                >
                  입금 확인
                </Button>
              </CardBody>
            </Collapse>
          </Card>

          <Card>
            <CardHeader>상태 처리</CardHeader>
            <CardBody>
              <div className="d-grid gap-4">
                <div role="group" aria-label="승인 처리" className="pb-3 border-bottom">
                  <div className="fw-semibold mb-2">승인 처리</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color="success"
                      onClick={() => approveMutation.mutate()}
                      disabled={actionBusy || !canApproveGroupReservationRequest(request)}
                    >
                      승인
                    </Button>
                    {canCancelGroupReservationApproval(request) && (
                      <Button
                        color="soft-warning"
                        onClick={() => cancelApprovalMutation.mutate()}
                        disabled={actionBusy}
                      >
                        승인 취소
                      </Button>
                    )}
                  </div>
                </div>

                <div role="group" aria-label="반려 처리" className="pb-3 border-bottom">
                  <div className="fw-semibold mb-2">반려 처리</div>
                  <Label htmlFor="reject-reason">반려 사유</Label>
                  <Input
                    id="reject-reason"
                    type="textarea"
                    rows={3}
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                  />
                  <Button
                    className="mt-2"
                    color="soft-danger"
                    onClick={submitReject}
                    disabled={actionBusy || !canRejectGroupReservationRequest(request)}
                  >
                    반려
                  </Button>
                </div>

                <div role="group" aria-label="되돌림 처리">
                  <div className="fw-semibold mb-2">되돌림 처리</div>
                  <Label htmlFor="revert-reason">되돌림 사유</Label>
                  <Input
                    id="revert-reason"
                    type="textarea"
                    rows={3}
                    value={revertReason}
                    onChange={(event) => setRevertReason(event.target.value)}
                  />
                  <Button
                    className="mt-2"
                    color="soft-warning"
                    onClick={submitRevert}
                    disabled={actionBusy || !canRevertGroupReservationRequest(request)}
                  >
                    연결 예약 되돌림
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </GroupReservationPageShell>
  );
}
